import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BillingIntervalToggle } from '@/features/billing/components/BillingIntervalToggle';
import { SeatStepper } from '@/features/billing/components/SeatStepper';
import { useBilling } from '@/features/billing/hooks/useBilling';
import { BUSINESS_PRICING } from '@/features/billing/lib/planPricing';
import {
  BUSINESS_INCLUDED_SEATS,
  BUSINESS_PRICES,
  BUSINESS_SEAT_PACK_SIZE,
  businessSeatLimit,
  MAX_SEAT_PACKS,
} from '@/features/billing/lib/seatPacks';
import type { SubscriptionType } from '@/features/billing/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  List,
  ListItem,
  SimpleGrid,
  Text,
} from '@/ui';
import { PanelContent } from '../../components/PanelContent';
import { PanelHeader } from '../../components/PanelHeader';
import { SectionHeader } from '../../components/SectionHeader';

// Buying a workspace provisions a *separate* account rather than upgrading the
// personal one, which is why it gets its own panel instead of sitting alongside
// the personal plan choices. The server creates the org from the checkout, so
// there's nothing to name or configure up front — only seats and cadence.
export const CreateOrganization = () => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'create_organization',
  });
  const [interval, setInterval] = useState<SubscriptionType>('yearly');
  // The stepper counts the packs being *added*; the included seats are fixed, so
  // stepping the total would bury what the buyer is actually choosing.
  const [packs, setPacks] = useState(0);
  const { redirectToCheckout } = useBilling();
  const { trackEvent } = useAnalytics();

  const seats = businessSeatLimit(packs);
  const { base, pack } = BUSINESS_PRICES[interval];
  const { period, packageId } = BUSINESS_PRICING[interval];
  const total = base + pack * packs;

  const handleCheckout = async () => {
    trackEvent('settings:click upgrade', {
      target: 'button',
      launch: 'settings:organizations',
    });
    // Land on the workspace list afterwards — the new org shows up there once
    // the checkout is verified.
    await redirectToCheckout(
      interval,
      { launch: 'settings:organizations' },
      packageId,
      { packs }
    );
  };

  const features = [
    t('feature_seats', {
      included: BUSINESS_INCLUDED_SEATS,
      packSize: BUSINESS_SEAT_PACK_SIZE,
      price: `$${pack}`,
      period: interval === 'yearly' ? t('per_year') : t('per_month'),
    }),
    t('feature_brand_kit'),
    t('feature_branded_links'),
  ];

  return (
    <>
      <PanelHeader>{t('heading')}</PanelHeader>
      <PanelContent>
        <SectionHeader first>{t('section_heading')}</SectionHeader>

        <Flex justify="center" mb="6">
          <BillingIntervalToggle
            annualLabel={t('annual')}
            ariaLabel={t('billing_interval')}
            monthlyLabel={t('monthly')}
            value={interval}
            onChange={setInterval}
          />
        </Flex>

        <Flex
          borderColor="gray.200"
          borderRadius="ah-xl"
          borderWidth="1px"
          direction="column"
          gap="5"
          p="6"
        >
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="6">
            <Flex direction="column" gap="1">
              <Text fontSize="2xl" fontWeight="medium" lineHeight="short">
                {t('plan_name')}
              </Text>
              <Flex align="baseline" gap="0.5">
                <Text fontSize="2xl" fontWeight="bold" lineHeight="short">
                  {`$${total}`}
                </Text>
                <Text color="customgray.middark" fontSize="sm">
                  {period}
                </Text>
              </Flex>
              <Text color="customgray.mid" fontSize="sm">
                {t(
                  interval === 'yearly' ? 'billed_annually' : 'billed_monthly',
                  {
                    included: BUSINESS_INCLUDED_SEATS,
                  }
                )}
              </Text>
            </Flex>

            <List display="flex" flexDirection="column" gap="3">
              {features.map((feature) => (
                <ListItem
                  alignItems="center"
                  display="flex"
                  gap="2.5"
                  key={feature}
                >
                  <Icon
                    color="brand.500"
                    flexShrink={0}
                    icon="Checkmark"
                    size="4"
                  />
                  <Text fontSize="sm">{feature}</Text>
                </ListItem>
              ))}
            </List>
          </SimpleGrid>

          <Divider />

          <Flex align="flex-start" gap="4" justify="space-between">
            <Box>
              <Text fontWeight="semibold">{t('seats_heading')}</Text>
              <Text color="customgray.mid" fontSize="sm">
                {t('seats_included', { count: BUSINESS_INCLUDED_SEATS })}
              </Text>
              {packs > 0 && (
                <Text color="customgray.mid" fontSize="sm">
                  {t('seats_packs', {
                    count: packs,
                    packSize: BUSINESS_SEAT_PACK_SIZE,
                  })}
                </Text>
              )}
            </Box>
            <Flex align="flex-end" direction="column" flexShrink="0" gap="1.5">
              <SeatStepper
                max={MAX_SEAT_PACKS}
                min={0}
                value={packs}
                decreaseLabel={t('seats_decrease', {
                  count: BUSINESS_SEAT_PACK_SIZE,
                })}
                increaseLabel={t('seats_increase', {
                  count: BUSINESS_SEAT_PACK_SIZE,
                })}
                onChange={setPacks}
              />
              {packs > 0 && (
                <Text color="customgray.mid" fontSize="sm">
                  {t('seats_total', { count: seats })}
                </Text>
              )}
            </Flex>
          </Flex>

          <Button onClick={handleCheckout}>{t('checkout')}</Button>
        </Flex>
      </PanelContent>
    </>
  );
};
